<!-- Taxes (Belka): proceeds, costs and income for the year, sold lots, dividends, year-end optimisation. -->
<script lang="ts">
  import * as E from "../core/engine";
  import type { RealizedEntry, YearStats } from "../core/types";
  import { app, need } from "../lib/state.svelte";
  import { download, toCsv } from "../lib/actions";
  import { fmtDate, fmtNum } from "../lib/format";
  import ViewHead from "../components/ViewHead.svelte";
  import Kpi from "../components/Kpi.svelte";
  import Card from "../components/Card.svelte";
  import Money from "../components/Money.svelte";
  import Signed from "../components/Signed.svelte";
  import RealizedTable from "./parts/RealizedTable.svelte";
  import HarvestCard from "./parts/HarvestCard.svelte";

  const m = $derived(need(app.model));
  const cfg = $derived(need(app.data).config);
  // tax always computed (regardless of the Belka toggle)
  const A = $derived(E.aggregate(m, app.accList, true));
  const exemptSel = $derived(app.accList.filter((a) => m.taxExempt.has(a)));
  const S = $derived(new Set(app.accList.filter((a) => !m.taxExempt.has(a)))); // IKE/IKZE are not part of PIT-38
  const thisYear = $derived(+m.today.slice(0, 4));
  const years = $derived([...new Set([...m.realized.filter((x) => S.has(x.account)).map((x) => x.year), ...m.incomes.filter((x) => S.has(x.account)).map((x) => x.year), thisYear])].sort((a, b) => b - a));
  const y = $derived(app.ui.taxYear && years.includes(app.ui.taxYear) ? app.ui.taxYear : years[0]);
  const rate = $derived(m.taxRate);
  const yy = $derived.by(() => {
    const Y0: Partial<YearStats> = A.years[y] || {};
    return { proceeds: Y0.taxProceeds || 0, cost: Y0.taxCost || 0, realized: Y0.taxRealized || 0, incomeGross: Y0.taxIncomeGross || 0, withheld: Y0.taxWithheld || 0 };
  });
  const lots = $derived(m.realized.filter((x) => S.has(x.account) && x.year === y).sort((a, b) => (a.closeDay < b.closeDay ? -1 : 1)));
  const income = $derived(m.incomes.filter((x) => S.has(x.account) && x.year === y));
  const base = $derived(Math.round(Math.max(0, yy.realized)));
  const tax = $derived(Math.round(base * rate));
  const e = $derived(m.idx(m.today));
  // losses from previous years (informational, 5 years)
  const losses = $derived(Object.values(A.years).filter((o) => o.year < y && o.year >= y - 5 && o.taxRealized < 0));
  const byAcc = $derived.by(() => {
    const out: Record<string, { p: number; c: number }> = {};
    for (const l of lots) { const k = l.account; (out[k] ||= { p: 0, c: 0 }); out[k].p += l.proceedsPLN; out[k].c += l.costPLN; }
    return Object.entries(out);
  });
  const settle = $derived(+(cfg.belka?.settlementDays || 0));
  const sellAll = $derived(A.L[e] - Object.values(A.years).filter((o) => o.year < y).reduce((s, o) => s + o.taxDue, 0));

  function exportCsv() {
    const mm = m;
    download(`sprzedaze_${y}.csv`, toCsv<RealizedEntry>(lots, [
      { k: "closeDay", label: "data_sprzedazy" }, { k: "taxDay", label: "data_podatkowa" }, { k: "openDay", label: "data_nabycia" }, { k: "account", label: "rachunek" }, { k: "symbol" },
      { label: "isin", v: (x) => mm.instr[x.symbol]?.isin }, { label: "ilosc", v: (x) => x.qty }, { label: "przychod_waluta", v: (x) => x.proceedsCur.toFixed(2) }, { k: "ccy", label: "waluta" },
      { label: "kurs_nbp_sprzedaz", v: (x) => x.fxSell }, { label: "przychod_pln", v: (x) => x.proceedsPLN.toFixed(2) }, { label: "koszt_waluta", v: (x) => x.costCur.toFixed(2) },
      { label: "kurs_nbp_zakup", v: (x) => x.fxBuy ?? "" }, { label: "koszt_pln", v: (x) => x.costPLN.toFixed(2) }, { label: "dochod_pln", v: (x) => x.gainPLN.toFixed(2) },
    ]));
  }
</script>

<ViewHead title="Podatki (Belka)" sub="stawka {fmtNum(rate * 100, 0)}% · kursy średnie NBP z dnia roboczego poprzedzającego {settle ? 'rozliczenie (T+' + cfg.belka?.settlementDays + ')' : 'transakcję'} · FIFO osobno dla każdego rachunku" />
<div class="row"><select id="taxYear" onchange={(ev) => (app.ui.taxYear = +ev.currentTarget.value)}>{#each years as x (x)}<option selected={x === y}>{x}</option>{/each}</select><span class="spacer"></span><button class="btn" id="expTax" onclick={exportCsv}>Eksport CSV (sprzedaże {y})</button></div>
<div class="kpis">
  <Kpi label="Przychód ze sprzedaży" sub="papiery wartościowe">{#snippet value()}<Money v={yy.proceeds} d={2} />{/snippet}</Kpi>
  <Kpi label="Koszty uzyskania" sub="koszt nabycia + prowizje">{#snippet value()}<Money v={yy.cost} d={2} />{/snippet}</Kpi>
  <Kpi label={yy.realized >= 0 ? "Dochód" : "Strata"} sub={yy.realized < 0 ? "do odliczenia w kolejnych 5 latach" : ""}>{#snippet value()}<Signed v={yy.realized} d={2} />{/snippet}</Kpi>
  <Kpi label="Podatek od zysków kapitałowych" sub="podstawa {fmtNum(base)} zł (zaokr.)">{#snippet value()}<Money v={tax} />{/snippet}</Kpi>
  <Kpi label="Dywidendy i odsetki">
    {#snippet value()}<Money v={yy.incomeGross} d={2} />{/snippet}
    {#snippet sub()}pobrany podatek <Money v={yy.withheld} d={2} /> · do dopłaty <Money v={Math.max(0, rate * yy.incomeGross - yy.withheld)} d={2} />{/snippet}
  </Kpi>
  {#if y === thisYear}
    <Kpi label="Gdybyś sprzedał wszystko dziś">
      {#snippet value()}<Money v={sellAll} />{/snippet}
      {#snippet sub()}w tym od niezrealizowanych <Money v={A.latent[e]} />{/snippet}
    </Kpi>
  {/if}
</div>
{#if exemptSel.length}<p class="small muted">Rachunki zwolnione z podatku Belki (IKE/IKZE) nie są wliczane: {exemptSel.join(", ")}.</p>{/if}
{#if losses.length}<div class="notice">Straty z lat {losses.map((o) => `${o.year} (${fmtNum(-o.taxRealized, 2)} zł)`).join(", ")} możesz odliczyć od dochodu — maksymalnie 50% straty w jednym roku (lub jednorazowo do 5 mln zł). Aplikacja nie odlicza ich automatycznie.</div>{/if}
<Card title="Podsumowanie wg rachunków — {y}">
  <div class="table-wrap"><table><thead><tr><th class="l">Rachunek</th><th>Przychód</th><th>Koszty</th><th>Dochód / strata</th><th class="l">Uwagi</th></tr></thead><tbody>
    {#each byAcc as [a, o] (a)}
      <tr><td class="l">{a}</td><td><Money v={o.p} d={2} /></td><td><Money v={o.c} d={2} /></td><td><Signed v={o.p - o.c} d={2} /></td><td class="l small muted">{a.startsWith("XTB") ? "polski broker — porównaj z PIT-8C" : "broker zagraniczny — rozliczasz samodzielnie w PIT-38"}</td></tr>
    {:else}
      <tr><td class="l muted" colspan="5">Brak sprzedaży w {y}.</td></tr>
    {/each}
  </tbody></table></div>
</Card>
{#if y === thisYear}<HarvestCard realized={yy.realized} {rate} />{/if}
{#if lots.length}<Card title="Sprzedane partie — {y}"><RealizedTable list={lots} withName /></Card>{/if}
{#if income.length}
  <Card title="Dywidendy i odsetki — {y}" hint="podatek pobrany osobnym wierszem (np. XTB od odsetek) jest wliczony w podsumowanie powyżej">
    <div class="table-wrap"><table><thead><tr><th class="l">Data</th><th class="l">Rachunek</th><th class="l">Rodzaj</th><th class="l">Instrument</th><th>Kwota (waluta)</th><th>Brutto PLN</th><th>Pobrany podatek PLN</th></tr></thead><tbody>
      {#each income as x, i (i)}
        <tr><td class="l">{fmtDate(x.day)}</td><td class="l">{x.account}</td><td class="l">{x.kind === "DIVIDEND" ? "dywidenda" : "odsetki"}</td><td class="l">{x.symbol || ""}</td><td><Money v={x.amt} d={2} ccy={x.ccy} /></td><td><Money v={x.gross} d={2} /></td><td><Money v={x.withheld} d={2} /></td></tr>
      {/each}
    </tbody></table></div>
  </Card>
{/if}
<p class="small muted">Wyliczenia są pomocnicze i nie stanowią porady podatkowej. Sprawdź je z informacjami od brokerów (PIT-8C) przed złożeniem zeznania.</p>
